import { Link } from 'react-router-dom'
import { User, CreditCard, Crown, ExternalLink } from 'lucide-react'

const planColors = {
  free: 'text-dark-400 bg-dark-700',
  basic: 'text-blue-400 bg-blue-500/20',
  pro: 'text-accent-400 bg-accent-500/20',
}

const statusColors = {
  active: 'text-green-400 bg-green-500/20',
  past_due: 'text-yellow-400 bg-yellow-500/20',
  canceled: 'text-red-400 bg-red-500/20',
  none: 'text-dark-400 bg-dark-700',
}

const UserTable = ({ users, loading }) => {
  if (loading && users.length === 0) {
    return (
      <div className="bg-dark-900 rounded-xl border border-dark-700 overflow-hidden">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-500 mx-auto"></div>
          <p className="mt-3 text-dark-400">Loading users...</p>
        </div>
      </div>
    )
  }

  if (users.length === 0) {
    return (
      <div className="bg-dark-900 rounded-xl border border-dark-700 overflow-hidden">
        <div className="p-8 text-center">
          <User className="w-12 h-12 text-dark-500 mx-auto" />
          <p className="mt-3 text-dark-400">No users found</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-dark-900 rounded-xl border border-dark-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-dark-700">
              <th className="text-left px-4 py-3 text-sm font-medium text-dark-400">User</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-dark-400">Plan</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-dark-400">Credits</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-dark-400">Status</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-dark-400">Joined</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-dark-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.uid} className="border-b border-dark-800 hover:bg-dark-800/50 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {user.photoURL ? (
                      <img 
                        src={user.photoURL} 
                        alt="" 
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-dark-700 flex items-center justify-center">
                        <User className="w-4 h-4 text-dark-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">{user.displayName || 'No name'}</p>
                      <p className="text-sm text-dark-400">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${planColors[user.plan] || planColors.free}`}>
                    {user.plan === 'pro' && <Crown className="w-3 h-3" />}
                    {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}
                  </span>
                  {user.planSource === 'admin_override' && (
                    <span className="ml-1 text-xs text-yellow-500">(override)</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-dark-500" />
                    <span className="font-mono text-sm">{user.creditsBalance.toLocaleString()}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${statusColors[user.subscriptionStatus] || statusColors.none}`}>
                    {user.subscriptionStatus ? user.subscriptionStatus.replace('_', ' ') : 'No subscription'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-dark-400">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/admin/users/${user.uid}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-dark-700 hover:bg-dark-600 rounded-lg text-sm transition-colors"
                  >
                    View
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default UserTable
